import { inject, Injectable } from "@angular/core";
import { GoogleCalendarEventsService } from "../../../api/services";
import { CreateEventDto, UpdateEventDto } from "../../../api/models";
import { Observable } from "rxjs";
import { GoogleCalendarEventsControllerUpdateEvent$Params } from "../../../api/fn/google-calendar-events/google-calendar-events-controller-update-event";
import { environment } from "../../../environments/environment";
import { GoogleCalendarEventsControllerDeleteEvent$Params } from "../../../api/fn/google-calendar-events/google-calendar-events-controller-delete-event";
import { GoogleCalendarEventsControllerCreateEvent$Params } from "../../../api/fn/google-calendar-events/google-calendar-events-controller-create-event";

@Injectable({
  providedIn: "root",
})
export class GoogleCalendarEventsApiService {
  /** Service for interacting with the Google Calendar Events API. */
  private readonly _googleCalendarEventsService: GoogleCalendarEventsService =
    inject(GoogleCalendarEventsService);

  /**
   * Creates a new event in the user's Google Calendar.
   *
   * @param body The details of the event to create.
   * @returns Observable that completes when\ the event is created.
   */
  public create$(body: CreateEventDto): Observable<void> {
    const params: GoogleCalendarEventsControllerCreateEvent$Params = {
      body,
    };

    return this._googleCalendarEventsService.googleCalendarEventsControllerCreateEvent(
      params,
    );
  }

  /**
   * Updates an existing Google Calendar event.
   *
   * @param organizerEmail The email address of the event organizer.
   * @param eventId The unique ID of the event in Google Calendar.
   * @param dynamoItemCreatedAt The timestamp of the original creation in DynamoDB.
   * @param body The updated event data.
   * @returns Observable that completes when the event is updated.
   */
  public update$(
    organizerEmail: string,
    eventId: string,
    dynamoItemCreatedAt: string,
    body: UpdateEventDto,
  ): Observable<void> {
    const params: GoogleCalendarEventsControllerUpdateEvent$Params = {
      tenant: environment.tenant,
      organizerEmail,
      eventId,
      createdAt: dynamoItemCreatedAt,
      body,
    };

    return this._googleCalendarEventsService.googleCalendarEventsControllerUpdateEvent(
      params,
    );
  }

  /**
   * Deletes a Google Calendar event.
   *
   * @param organizerEmail The email address of the event organizer.
   * @param eventId The unique ID of the event in Google Calendar.
   * @param dynamoItemCreatedAt The timestamp of the original creation in DynamoDB.
   * @param entity The type of entity associated with the event ("visit" or "test").
   * @returns Observable that completes when the event is deleted.
   */
  public delete$(
    organizerEmail: string,
    eventId: string,
    dynamoItemCreatedAt: string,
    entity: "visit" | "test",
  ): Observable<void> {
    const params: GoogleCalendarEventsControllerDeleteEvent$Params = {
      tenant: environment.tenant,
      organizerEmail,
      eventId,
      createdAt: dynamoItemCreatedAt,
      entity,
    };

    return this._googleCalendarEventsService.googleCalendarEventsControllerDeleteEvent(
      params,
    );
  }
}
